import { _PdfDictionary } from './pdf-primitives';
import { _byteArrayToHexString, _bytesToString, _isNullOrUndefined } from './utils';
export class _PdfBaseStream {
    constructor() {
        this._isCompress = true;
        this._isImage = false;
    }
    getByte() {
        return null;
    }
    getBytes(length) {
        return null;
    }
    get length() {
        throw new Error('Abstract getter `length` accessed');
    }
    get isEmpty() {
        throw new Error('Abstract getter `isEmpty` accessed');
    }
    get isDataLoaded() {
        return true;
    }
    peekByte() {
        const peekedByte = this.getByte();
        if (peekedByte !== -1) {
            this.offset--;
        }
        return peekedByte;
    }
    peekBytes(length) {
        const bytes = this.getBytes(length);
        this.offset -= bytes.length;
        return bytes;
    }
    getUnsignedInteger16() {
        const b0 = this.getByte();
        const b1 = this.getByte();
        if (b0 === -1 || b1 === -1) {
            return -1;
        }
        return (b0 << 8) + b1;
    }
    getInt32() {
        const b0 = this.getByte();
        const b1 = this.getByte();
        const b2 = this.getByte();
        const b3 = this.getByte();
        return (b0 << 24) + (b1 << 16) + (b2 << 8) + b3;
    }
    getByteRange(begin, end) {
        return null;
    }
    makeSubStream(start, length, dictionary) {
        return null;
    }
    readBlock() {
        return null;
    }
    reset() {
        return null;
    }
    moveStart() {
        return null;
    }
    getString(isHex = false, bytes) {
        if (typeof bytes === 'undefined' || bytes === null) {
            bytes = this.getBytes();
        }
        if (isHex) {
            return _byteArrayToHexString(bytes);
        }
        else {
            return _bytesToString(bytes);
        }
    }
    skip(n) {
        this.offset += n || 1;
    }
    getBaseStreams() {
        return null;
    }
}
export class _PdfStream extends _PdfBaseStream {
    constructor(arrayBuffer, dictionary, start, length) {
        super();
        this.isImageStream = false;
        this.bytes = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
        if (typeof start !== 'undefined') {
            this.start = start;
        }
        else {
            this.start = 0;
        }
        this.position = this.start;
        this.end = start + length || this.bytes.length;
        this.dictionary = dictionary;
    }
    /**
     * Gets the position of the stream.
     *
     * @returns {number} offset position.
     */
    get position() {
        return this.offset;
    }
    /**
     * Sets the position of the stream.
     *
     * @param {number} value offset position.
     */
    set position(value) {
        this.offset = value;
    }
    /**
     * Gets the length of the stream (Read only).
     *
     * @returns {number} length.
     */
    get length() {
        return this.end - this.start;
    }
    /**
     * Gets a value indicating whether the stream is empty (Read only).
     *
     * @returns {boolean} stream empty or not.
     */
    get isEmpty() {
        return this.length === 0;
    }
    getByte() {
        if (this.position >= this.end) {
            return -1;
        }
        return this.bytes[this.position++];
    }
    getBytes(length) {
        const bytes = this.bytes;
        const position = this.position;
        const strEnd = this.end;
        if (!length) {
            return bytes.subarray(position, strEnd);
        }
        let end = position + length;
        if (end > strEnd) {
            end = strEnd;
        }
        this.position = end;
        return bytes.subarray(position, end);
    }
    getByteRange(begin, end) {
        if (begin < 0) {
            begin = 0;
        }
        if (end > this.end) {
            end = this.end;
        }
        return this.bytes.subarray(begin, end);
    }
    reset() {
        this.position = this.start;
    }
    moveStart() {
        this.start = this.position;
    }
    makeSubStream(start, length, dictionary = null) {
        return new _PdfStream(this.bytes.buffer, dictionary, start, length);
    }
    readBlock() {
        throw new Error('Abstract method `readBlock` called');
    }
    _clearStream() {
        if (this.dictionary !== null && typeof this.dictionary !== 'undefined' && this.dictionary.has('Filter')) {
            delete this.dictionary._map.Filter;
        }
        this._isCompress = true;
        this.dictionary._updated = true;
    }
    _write(text) {
        this.bytes = new Uint8Array(text.length);
        for (let i = 0; i < text.length; i++) {
            this.bytes[i] = text.charCodeAt(i);
        }
        this.end = this.bytes.length;
        this.dictionary._updated = true;
    }
    _writeBytes(data) {
        this.bytes = new Uint8Array(data);
        this.end = this.bytes.length;
        this.dictionary._updated = true;
    }
}
export class _PdfContentStream extends _PdfBaseStream {
    get length() {
        return this._bytes.length;
    }
    constructor(bytes) {
        super();
        if (_isNullOrUndefined(bytes)) {
            this._bytes = bytes;
        }
        else {
            this._bytes = [];
        }
        this.dictionary = new _PdfDictionary();
        this.dictionary._updated = true;
    }
    write(data) {
        if (typeof data === 'string') {
            for (let i = 0; i < data.length; i++) {
                this._bytes.push(data.charCodeAt(i));
            }
        }
        else {
            this._bytes.push(...data);
        }
        this.dictionary._updated = true;
    }
    getString(isHex = false) {
        const bytes = new Uint8Array(this._bytes);
        if (typeof bytes === 'undefined' || bytes === null || typeof bytes.length === 'undefined') {
            throw new Error('Invalid argument for bytesToString');
        }
        if (isHex) {
            return _byteArrayToHexString(bytes);
        }
        else {
            const len = bytes.length;
            const max = 8192;
            if (len < max) {
                return String.fromCharCode.apply(null, bytes);
            }
            const stringBuffer = [];
            for (let i = 0; i < len; i += max) {
                stringBuffer.push(String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + max, len))));
            }
            return stringBuffer.join('');
        }
    }
}
export class _PdfNullStream extends _PdfStream {
    constructor() {
        super(new Uint8Array(0));
    }
}
